import { attachmentsApi } from '../api/attachments'
import { issuesApi, type ReadyForNextStagePayload } from '../api/issues'
import type { TimeLogStatus, WorkType } from '../types'

export type ProofType = 'SCREENSHOT' | 'SCREEN_RECORDING_LINK' | 'NO_PROOF_REQUIRED'

export type TransitionProofInput = {
  type: ProofType
  screenshotFiles?: File[]
  screenRecordingLink?: string
  noProofReason?: string
}

export type TransitionPopupPayload = {
  targetStatusId: string
  labelIds?: string[]
  timeLog?: {
    workType: WorkType
    status: TimeLogStatus
    hours: number
    progressPercent?: number
    progressNotes?: string
    completedDetails?: string
    loggedDate: string
  }
  proof?: TransitionProofInput
}

function buildInternalNoteContent(options: {
  issueTitle: string
  targetStatusName: string
  payload: TransitionPopupPayload
  uploadedAttachments: Array<{ filename: string; fileUrl: string }>
}) {
  const lines: string[] = []

  if (options.payload.timeLog) {
    const details = options.payload.timeLog.status === 'COMPLETED'
      ? options.payload.timeLog.completedDetails?.trim() || ''
      : options.payload.timeLog.progressNotes?.trim() || ''

    if (details) {
      lines.push(details)
    }
  }

  if (options.payload.proof) {
    if (lines.length > 0) {
      lines.push('')
    }

    if (options.payload.proof.type === 'SCREENSHOT') {
      for (const attachment of options.uploadedAttachments) {
        lines.push(`![${attachment.filename}](${attachment.fileUrl})`)
      }
    } else if (options.payload.proof.type === 'SCREEN_RECORDING_LINK') {
      const recordingLink = options.payload.proof.screenRecordingLink?.trim() || ''
      lines.push(`Recording: [Open screen recording](${recordingLink})`)
    } else {
      const reason = options.payload.proof.noProofReason?.trim() || ''
      if (reason) {
        lines.push(`Proof not required: ${reason}`)
      }
    }
  }

  return lines.join('\n').trim()
}

export async function submitStatusTransitionWithInternalNote(options: {
  issueId: string
  issueTitle: string
  targetStatusName: string
  payload: TransitionPopupPayload
}) {
  const uploadedAttachments: Array<{ id: string; filename: string; fileUrl: string }> = []

  try {
    if (options.payload.proof?.type === 'SCREENSHOT' && options.payload.proof.screenshotFiles?.length) {
      for (const file of options.payload.proof.screenshotFiles) {
        const uploaded = await attachmentsApi.uploadAttachment(options.issueId, file)
        uploadedAttachments.push({
          id: uploaded.data.data.id,
          filename: uploaded.data.data.filename,
          fileUrl: uploaded.data.data.fileUrl,
        })
      }
    }

    const internalNoteContent = buildInternalNoteContent({
      issueTitle: options.issueTitle,
      targetStatusName: options.targetStatusName,
      payload: options.payload,
      uploadedAttachments,
    })

    const requestPayload: ReadyForNextStagePayload = {
      targetStatusId: options.payload.targetStatusId,
      labelIds: options.payload.labelIds,
      timeLog: options.payload.timeLog,
      ...(internalNoteContent ? { internalNote: { content: internalNoteContent } } : {}),
    }

    return await issuesApi.readyForNextStage(options.issueId, requestPayload)
  } catch (error) {
    if (uploadedAttachments.length > 0) {
      await Promise.allSettled(
        uploadedAttachments.map((attachment) => attachmentsApi.deleteAttachment(options.issueId, attachment.id))
      )
    }
    throw error
  }
}
